import React from 'react'
import { useApp } from '../context/AppContext'
import { Icon, useT, useIsMobile } from '../shared'
import { Card, Btn, Badge, Progress } from '../shared/components'
import {
  listDocModules, createDocModule, seedDefaultModules, deleteDocModule,
  listDocs, uploadDoc, updateDoc, approveDoc, rejectDoc, uploadDocVersion,
  getDocDownloadUrl,
} from '../api/cms.api'
import { tintBg, tintFg } from '../lib/tint'

/* ============================================================
   FR-31 — Doc Library (CMS) — OSCT-2 design
   Module sidebar + rich document rows + upload/detail modals.
   Program-isolated (a program is a single OPRC level), wired to
   the real backend.
   ============================================================ */

const STATUS_TONE = { DRAFT: 'default', PENDING_REVIEW: 'warning', APPROVED: 'success', REJECTED: 'danger' }
function statusLabel(s, t) {
  return { DRAFT: t.stDraft, PENDING_REVIEW: t.stPendingReview, APPROVED: t.stApproved, REJECTED: t.stRejected }[s] || s
}
const FILE_ICONS = {
  PDF: { icon: '📄', color: tintFg('#B53A2C'), soft: tintBg('#B53A2C') },
  DOCX: { icon: '📝', color: tintFg('#1E4A6B'), soft: tintBg('#1E4A6B') },
  DOC: { icon: '📝', color: tintFg('#1E4A6B'), soft: tintBg('#1E4A6B') },
  PPTX: { icon: '📊', color: tintFg('#C97B00'), soft: tintBg('#C97B00') },
  PPT: { icon: '📊', color: tintFg('#C97B00'), soft: tintBg('#C97B00') },
  XLSX: { icon: '📈', color: tintFg('#2E7D5B'), soft: tintBg('#2E7D5B') },
  XLS: { icon: '📈', color: tintFg('#2E7D5B'), soft: tintBg('#2E7D5B') },
}
const fileIcon = (type) => FILE_ICONS[type] || FILE_ICONS.PDF
const FILE_TYPES = ['PDF', 'DOCX', 'PPTX', 'XLSX', 'DOC', 'PPT', 'XLS']

function fmtBytes(b) {
  if (!b) return '—'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}
function fmtDate(d, lang) {
  if (!d) return ''
  try { return new Date(d).toLocaleDateString(lang === 'id' ? 'id-ID' : 'en-US', { day: 'numeric', month: 'short' }) }
  catch { return '' }
}

export default function CMSScreen() {
  const t = useT()
  const { role, lang, currentProgram, currentProgramId } = useApp()
  const isMobile = useIsMobile()
  const isAdmin = role === 'admin'
  const isStaff = role === 'admin' || role === 'trainer'

  const [modules, setModules] = React.useState([])
  const [docs, setDocs] = React.useState([])
  const [selectedMod, setSelectedMod] = React.useState(null)
  const [search, setSearch] = React.useState('')
  const [filterType, setFilterType] = React.useState('all')
  const [filterStatus, setFilterStatus] = React.useState('all')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(null)
  const [busy, setBusy] = React.useState(false)
  const [showUpload, setShowUpload] = React.useState(false)
  const [detailDoc, setDetailDoc] = React.useState(null)

  const reload = React.useCallback(() => {
    if (!currentProgramId) return
    setLoading(true); setError(null)
    Promise.all([listDocModules(currentProgramId), listDocs(currentProgramId)])
      .then(([m, d]) => { setModules(m); setDocs(d) })
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId])
  React.useEffect(() => { reload() }, [reload])

  // May be null: not every programme has tiers. Modules created for such a
  // programme carry no tier either.
  const level = currentProgram?.level ?? modules[0]?.level ?? null
  const moduleCount = (id) => docs.filter((d) => d.module_id === id).length
  const modulePending = (id) => docs.some((d) => d.module_id === id && d.status === 'PENDING_REVIEW')

  const visibleDocs = docs.filter((d) =>
    (selectedMod === null || d.module_id === selectedMod) &&
    (search === '' || d.name.toLowerCase().includes(search.toLowerCase())) &&
    (filterType === 'all' || d.file_type === filterType) &&
    (filterStatus === 'all' || d.status === filterStatus)
  )

  const stats = [
    { label: t.cmsStatTotal, value: docs.length },
    { label: t.cmsStatPending, value: docs.filter((d) => d.status === 'PENDING_REVIEW').length },
    { label: t.cmsStatApproved, value: docs.filter((d) => d.status === 'APPROVED').length },
    { label: t.cmsStatStorage, value: fmtBytes(docs.reduce((s, d) => s + (d.size_bytes || 0), 0)) },
  ]

  const act = async (fn) => {
    setBusy(true); setError(null)
    try { await fn(); reload() }
    catch (ex) { setError(ex?.response?.data?.error || ex.message) }
    finally { setBusy(false) }
  }
  const doApprove = (id) => act(() => approveDoc(currentProgramId, id))
  const doReject = (id) => {
    const reason = window.prompt(t.cmsRejectPrompt)
    if (!reason) return
    return act(() => rejectDoc(currentProgramId, id, reason))
  }
  const doSubmit = (id) => act(() => updateDoc(currentProgramId, id, { submit: true }))
  const doFeature = (d) => act(() => updateDoc(currentProgramId, d.id, { featured: !d.featured }))
  const doNewVersion = (id) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.onchange = () => { if (input.files?.[0]) act(() => uploadDocVersion(currentProgramId, id, input.files[0])) }
    input.click()
  }
  const download = async (id) => {
    // Open window synchronously (inside user gesture) before the async API call,
    // otherwise pop-up blockers will block window.open called after await.
    const win = window.open('', '_blank', 'noopener')
    try {
      const { url } = await getDocDownloadUrl(currentProgramId, id)
      win.location.href = url
    } catch (e) {
      win?.close()
      setError(e?.response?.data?.error || e.message)
    }
  }
  const addModule = () => {
    const name = window.prompt(t.cmsNewModuleName + ':')
    if (!name?.trim()) return
    act(() => createDocModule(currentProgramId, { name: name.trim() }))
  }
  const removeModule = (m) => {
    if (moduleCount(m.id) > 0) return
    if (window.confirm(t.cmsDeleteModuleConfirm)) act(() => deleteDocModule(currentProgramId, m.id))
  }

  const pendingVisible = isAdmin && visibleDocs.filter((d) => d.status === 'PENDING_REVIEW').length

  return (
    <div className="main-inner">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">{isAdmin ? t.cmsContentMgmt : t.cmsLibraryEyebrow} · L{level}</div>
          <h1 className="h1" style={{ marginTop: 6 }}>{t.cmsTitle}</h1>
          <div className="muted mt-1">{isStaff ? t.cmsHeaderDesc : t.cmsDescParticipant}</div>
        </div>
        <div className="page-header-actions">
          <div className="search-wrap">
            <Icon name="search" size={14} />
            <input className="search-input" placeholder={t.cmsSearch} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          {isStaff && <Btn variant="primary" icon={<Icon name="upload" size={14} />} disabled={!currentProgramId} onClick={() => setShowUpload(true)}>{t.cmsUpload}</Btn>}
        </div>
      </div>

      {error && <div className="banner red mt-1" style={{ marginBottom: 12 }}><Icon name="alert" />{error}</div>}

      {/* Stats row (staff only) */}
      {isStaff && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 12 }}>
          {stats.map((s) => (
            <div key={s.label} style={{ padding: '14px 18px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, boxShadow: 'var(--shadow-xs)' }}>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-0.5px', marginTop: 6 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="muted mt-5">{t.loadingText}</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr', gap: 16, marginTop: isStaff ? 20 : 8 }}>
          {/* Module sidebar */}
          <Card
            title={`${t.cmsModule} L${level}`}
            eyebrow={`${modules.length} ${t.cmsModulesWord}`}
            action={isAdmin ? <button className="icon-btn" title={t.cmsAdd} onClick={addModule}><Icon name="plus" size={14} /></button> : null}
            noPad
          >
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {modules.length === 0 && isAdmin && (
                <div style={{ padding: 14 }}>
                  <Btn variant="primary" size="sm" block disabled={busy} onClick={() => act(() => seedDefaultModules(currentProgramId))}>{t.cmsSeedDefaults}</Btn>
                </div>
              )}
              <button onClick={() => setSelectedMod(null)} style={{
                width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none',
                background: selectedMod === null ? 'var(--program-color-soft)' : 'transparent',
                borderLeft: `3px solid ${selectedMod === null ? 'var(--program-color)' : 'transparent'}`,
                cursor: 'pointer', fontSize: 13, fontWeight: selectedMod === null ? 500 : 400,
                color: selectedMod === null ? 'var(--program-color-text)' : 'var(--text-2)',
                display: 'flex', justifyContent: 'space-between',
              }}>
                <span>{t.cmsAllModules}</span>
                <span style={{ fontSize: 11, color: 'var(--text-3)' }}>{docs.length} {t.cmsFilesWord}</span>
              </button>
              <div style={{ height: 1, background: 'var(--border-subtle)' }} />
              {modules.map((m, i) => {
                const active = selectedMod === m.id
                return (
                  <button key={m.id} onClick={() => setSelectedMod(m.id)} style={{
                    width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none',
                    background: active ? 'var(--program-color-soft)' : 'transparent',
                    borderLeft: `3px solid ${active ? 'var(--program-color)' : 'transparent'}`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span className="mono" style={{ fontSize: 10, color: 'var(--text-4)', minWidth: 22 }}>{String(i + 1).padStart(2, '0')}</span>
                    <span style={{ flex: 1, fontSize: 12, color: active ? 'var(--program-color-text)' : 'var(--text-2)', fontWeight: active ? 500 : 400, lineHeight: 1.3 }}>{m.name}</span>
                    {modulePending(m.id) && isAdmin && <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--amber)' }} />}
                    <span style={{ fontSize: 10, color: 'var(--text-4)', minWidth: 16, textAlign: 'right' }}>{moduleCount(m.id)}</span>
                    {isAdmin && moduleCount(m.id) === 0 && (
                      <span className="icon-btn" title={t.cmsDelete} onClick={(e) => { e.stopPropagation(); removeModule(m) }} style={{ color: 'var(--danger)' }}><Icon name="trash" size={12} /></span>
                    )}
                  </button>
                )
              })}
            </div>
          </Card>

          {/* Document list */}
          <div className="col gap-3">
            {/* Filters */}
            <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
              <select className="select" style={{ width: 140 }} value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                <option value="all">{t.cmsAllTypes}</option>
                {FILE_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
              </select>
              {isStaff && (
                <select className="select" style={{ width: 170 }} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="all">{t.cmsAllStatuses}</option>
                  {['DRAFT', 'PENDING_REVIEW', 'APPROVED', 'REJECTED'].map((s) => <option key={s} value={s}>{statusLabel(s, t)}</option>)}
                </select>
              )}
            </div>

            {/* Pending alert (admin) */}
            {pendingVisible > 0 && (
              <div style={{ padding: '12px 16px', background: 'var(--amber-soft)', border: '1px solid var(--amber)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
                <Icon name="clock" size={14} style={{ color: 'var(--amber)', flexShrink: 0 }} />
                <span><b>{pendingVisible}</b> {t.cmsPendingAlert}</span>
                <button onClick={() => setFilterStatus('PENDING_REVIEW')} style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--amber)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>{t.cmsShowPending}</button>
              </div>
            )}

            {visibleDocs.length === 0 ? (
              <div className="empty" style={{ padding: 48 }}>
                <Icon name={isStaff ? 'upload' : 'doc'} size={20} style={{ color: 'var(--text-4)' }} />
                <div style={{ marginTop: 10 }}>{isStaff ? t.cmsNoDocsHere : t.cmsNoDocs}</div>
                {isStaff && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{t.cmsUploadFirstHint}</div>}
                {isStaff && modules.length > 0 && <Btn variant="primary" icon={<Icon name="upload" size={14} />} style={{ marginTop: 14 }} onClick={() => setShowUpload(true)}>{t.cmsUpload}</Btn>}
              </div>
            ) : (
              <Card noPad>
                <div className="card-header">
                  <div className="h3" style={{ flex: 1 }}>{selectedMod !== null ? modules.find((m) => m.id === selectedMod)?.name : t.cmsAllLevelDocs}</div>
                  <span className="muted" style={{ fontSize: 12 }}>{visibleDocs.length} {t.cmsFilesWord}</span>
                </div>
                <div>
                  {visibleDocs.map((doc) => (
                    <DocRow key={doc.id} doc={doc} t={t} lang={lang} isAdmin={isAdmin} isStaff={isStaff} busy={busy}
                      onView={() => setDetailDoc(doc)} onDownload={() => download(doc.id)}
                      onApprove={() => doApprove(doc.id)} onReject={() => doReject(doc.id)}
                      onSubmit={() => doSubmit(doc.id)} onFeature={() => doFeature(doc)} onNewVersion={() => doNewVersion(doc.id)} />
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      )}

      {showUpload && (
        <UploadModal t={t} pid={currentProgramId} modules={modules} preselect={selectedMod}
          onClose={() => setShowUpload(false)} onDone={() => { setShowUpload(false); reload() }} />
      )}
      {detailDoc && (
        <DetailModal t={t} lang={lang} doc={detailDoc} isAdmin={isAdmin} isStaff={isStaff}
          onClose={() => setDetailDoc(null)}
          onDownload={() => download(detailDoc.id)}
          onApprove={() => { doApprove(detailDoc.id); setDetailDoc(null) }}
          onReject={() => { doReject(detailDoc.id); setDetailDoc(null) }}
          onFeature={() => { doFeature(detailDoc); setDetailDoc(null) }} />
      )}
    </div>
  )
}

// ─── Document row ────────────────────────────────────────────
function DocRow({ doc, t, lang, isAdmin, isStaff, busy, onView, onDownload, onApprove, onReject, onSubmit, onFeature, onNewVersion }) {
  const [hov, setHov] = React.useState(false)
  const fc = fileIcon(doc.file_type)
  const mine = isStaff // backend already scopes; trainers see own — keep actions available to staff
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
      background: hov ? 'var(--surface-2)' : 'transparent', borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.12s',
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 8, flexShrink: 0, background: fc.soft, color: fc.color, display: 'grid', placeItems: 'center', fontSize: 18 }}>{fc.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row gap-2">
          <span style={{ fontWeight: 500, fontSize: 13 }} className="text-truncate">{doc.name}</span>
          {doc.featured && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: 'var(--amber-soft)', color: 'var(--amber)', fontWeight: 600, flexShrink: 0 }}>★ {t.cmsFeatured}</span>}
        </div>
        <div className="row gap-3 muted mt-1" style={{ fontSize: 11, flexWrap: 'wrap' }}>
          <span className="mono" style={{ background: fc.soft, color: fc.color, padding: '1px 5px', borderRadius: 3, fontWeight: 500 }}>{doc.file_type}</span>
          <span>{fmtBytes(doc.size_bytes)}</span>
          <span>{fmtDate(doc.created_at, lang)}</span>
          <span>v{doc.version}</span>
          {isStaff && doc.uploader?.full_name && <span>{t.cmsByWord} {doc.uploader.full_name}</span>}
        </div>
        {doc.status === 'REJECTED' && doc.reject_reason && (
          <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4, fontStyle: 'italic' }}>{statusLabel('REJECTED', t)}: {doc.reject_reason}</div>
        )}
      </div>
      <Badge tone={STATUS_TONE[doc.status]} dot>{statusLabel(doc.status, t)}</Badge>
      <div className="row gap-1" style={{ opacity: hov ? 1 : 0.25, transition: 'opacity 0.12s', flexShrink: 0 }}>
        {isStaff && <button className="icon-btn" onClick={onView} title={t.cmsViewDetails}><Icon name="doc" size={14} /></button>}
        <button className="icon-btn" onClick={onDownload} title={t.cmsDownload}><Icon name="download" size={14} /></button>
        {isStaff && (doc.status === 'DRAFT' || doc.status === 'REJECTED') && (
          <button className="icon-btn" onClick={onSubmit} disabled={busy} title={t.cmsSubmit} style={{ color: 'var(--program-color)' }}><Icon name="send" size={13} /></button>
        )}
        {isStaff && (doc.status === 'REJECTED' || doc.status === 'APPROVED') && (
          <button className="icon-btn" onClick={onNewVersion} disabled={busy} title={t.cmsNewVersion}><Icon name="upload" size={13} /></button>
        )}
        {isAdmin && doc.status === 'PENDING_REVIEW' && (
          <>
            <button className="icon-btn" onClick={onApprove} disabled={busy} title={t.cmsApprove} style={{ color: 'var(--success)' }}><Icon name="check" size={14} /></button>
            <button className="icon-btn" onClick={onReject} disabled={busy} title={t.cmsReject} style={{ color: 'var(--danger)' }}><Icon name="close" size={14} /></button>
          </>
        )}
        {isAdmin && (
          <button className="icon-btn" onClick={onFeature} disabled={busy} title={t.cmsFeatured} style={{ color: doc.featured ? 'var(--amber)' : 'var(--text-3)' }}>{doc.featured ? '★' : '☆'}</button>
        )}
      </div>
    </div>
  )
}

// ─── Upload modal (drag-drop, single file → backend) ─────────
function UploadModal({ t, pid, modules, preselect, onClose, onDone }) {
  const [file, setFile] = React.useState(null)
  const [dragOver, setDragOver] = React.useState(false)
  const [mod, setMod] = React.useState(preselect || modules[0]?.id || '')
  const [title, setTitle] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [err, setErr] = React.useState(null)
  const fileRef = React.useRef()
  const MAX = 25 * 1024 * 1024
  const tooBig = file && file.size > MAX

  const pick = (f) => { if (f) { setFile(f); setErr(null) } }

  const go = async (submit) => {
    if (!file || !mod || tooBig) return
    setBusy(true); setErr(null)
    try {
      const created = await uploadDoc(pid, { moduleId: mod, name: title || undefined, file })
      if (submit) await updateDoc(pid, created.id, { submit: true })
      onDone()
    } catch (ex) {
      const d = ex?.response?.data
      setErr(d?.suggestion ? `${d.error} — "${d.suggestion}"` : (d?.error || ex.message))
    } finally { setBusy(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(11,20,23,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', borderRadius: 20, boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', border: '1px solid var(--border-subtle)' }}>
        <div style={{ padding: '24px 28px 0' }}>
          <div className="row between">
            <div>
              <div className="eyebrow">{t.cmsUpload}</div>
              <div className="h2 mt-1">{t.cmsUpload}</div>
            </div>
            <button className="icon-btn" onClick={onClose}><Icon name="close" size={14} /></button>
          </div>
          <div className="field mt-4">
            <label className="field-label">{t.cmsTargetModule} *</label>
            <select className="select mt-1" value={mod} onChange={(e) => setMod(e.target.value)}>
              {modules.map((m, i) => <option key={m.id} value={m.id}>{String(i + 1).padStart(2, '0')} · {m.name}</option>)}
            </select>
          </div>
        </div>
        <div style={{ padding: '16px 28px 28px' }}>
          {err && <div className="banner red" style={{ marginBottom: 12 }}><Icon name="alert" />{err}</div>}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]) }}
            onClick={() => fileRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? 'var(--program-color)' : 'var(--border-strong)'}`, background: dragOver ? 'var(--program-color-soft)' : 'var(--surface-2)', borderRadius: 14, padding: 32, textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => pick(e.target.files?.[0])} />
            <div style={{ width: 48, height: 48, borderRadius: 12, margin: '0 auto 12px', background: 'var(--program-color-soft)', color: 'var(--program-color-deep)', display: 'grid', placeItems: 'center' }}><Icon name="upload" size={22} /></div>
            <div className="h3">{file ? file.name : t.cmsDropFiles}</div>
            <div className="muted mt-1" style={{ fontSize: 12 }}>{t.cmsDropHint}</div>
            {tooBig && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>⚠ {t.cmsExceeds}</div>}
          </div>
          <div className="field mt-4">
            <label className="field-label">{t.cmsDocTitleLabel} <span className="muted">({t.cmsDocTitleHint})</span></label>
            <input className="input mt-1" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t.cmsDefaultFileName} />
          </div>
          <div className="row between" style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border-subtle)' }}>
            <Btn variant="ghost" onClick={onClose} disabled={busy}>{t.cmsClose}</Btn>
            <div className="row gap-2">
              <Btn disabled={!file || !mod || tooBig || busy} onClick={() => go(false)}>{t.cmsSaveDraft}</Btn>
              <Btn variant="primary" disabled={!file || !mod || tooBig || busy} onClick={() => go(true)}>{busy ? t.cmsUploading : t.cmsSubmitReview}</Btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Doc detail modal ────────────────────────────────────────
function DetailModal({ t, lang, doc, isAdmin, isStaff, onClose, onDownload, onApprove, onReject, onFeature }) {
  const fc = fileIcon(doc.file_type)
  const rows = [
    { l: t.cmsStatusCol, v: <Badge tone={STATUS_TONE[doc.status]} dot>{statusLabel(doc.status, t)}</Badge> },
    { l: t.cmsUploadedBy, v: doc.uploader?.full_name || '—' },
    { l: t.cmsVersionLabel, v: `v${doc.version}` },
    { l: t.cmsSizeCol, v: fmtBytes(doc.size_bytes) },
    { l: t.cmsTypeCol, v: doc.file_type },
    { l: t.cmsFeatured, v: doc.featured ? '★' : '—' },
  ]
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(11,20,23,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', borderRadius: 20, boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: 520, border: '1px solid var(--border-subtle)' }}>
        <div style={{ padding: '20px 24px', background: fc.soft, borderRadius: '20px 20px 0 0', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 12, flexShrink: 0, background: 'white', color: fc.color, display: 'grid', placeItems: 'center', fontSize: 26, boxShadow: 'var(--shadow-sm)' }}>{fc.icon}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{doc.name}</div>
            <div className="row gap-2 muted mt-1" style={{ fontSize: 12 }}>
              <span style={{ background: fc.soft, color: fc.color, padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>{doc.file_type}</span>
              <span>{fmtBytes(doc.size_bytes)}</span>
              <span>{fmtDate(doc.created_at, lang)}</span>
            </div>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', rowGap: 14, columnGap: 20, fontSize: 13 }}>
            {rows.map((r) => (
              <div key={r.l}>
                <div className="eyebrow" style={{ marginBottom: 2 }}>{r.l}</div>
                <div style={{ fontWeight: 500 }}>{r.v}</div>
              </div>
            ))}
          </div>
          {doc.status === 'REJECTED' && doc.reject_reason && (
            <div style={{ marginTop: 16, padding: '12px 14px', background: 'var(--danger-soft)', border: '1px solid var(--danger)', borderRadius: 10, fontSize: 12, color: 'var(--danger)' }}>
              <b>{statusLabel('REJECTED', t)}:</b> {doc.reject_reason}
            </div>
          )}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Btn icon={<Icon name="download" size={14} />} onClick={onDownload}>{t.cmsDownload}</Btn>
          {isAdmin && <Btn onClick={onFeature}>{doc.featured ? '★ ' + t.cmsFeatured : '☆ ' + t.cmsFeatured}</Btn>}
          <div style={{ flex: 1 }} />
          {isAdmin && doc.status === 'PENDING_REVIEW' && (
            <>
              <Btn onClick={onReject} style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} icon={<Icon name="close" size={14} />}>{t.cmsReject}</Btn>
              <Btn variant="primary" onClick={onApprove} icon={<Icon name="check" size={14} />}>{t.cmsApprove}</Btn>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
